import React from 'react';
import { Tag, Tooltip } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { Link } from 'react-router-dom';

import { pathToUrl } from '../../utils/router';
import { PageRoutesEnum } from '../../utils/routes';

import { MatchingSource, Recommendation } from './matching.types';
import {
  budgetLabels,
  counterpartName,
  counterpartUuid,
  matchingDetailPath,
  PageWideState,
  visibleRowFlags,
} from './matchingPresentation';
import { RowStatusTags } from './MatchingStatus';

export const useRecommendationsTableColumnDefinitions = (
  source: MatchingSource,
  search: string,
  pageWide: Set<PageWideState>
): ColumnsType<Recommendation> => [
  {
    title: source.type === 'project' ? 'Candidate' : 'Project',
    key: 'name',
    render: (_, row) => {
      const flags = visibleRowFlags(row, pageWide);
      return (
        <>
          <Link
            className="matching-name"
            to={`${matchingDetailPath(source, counterpartUuid(row))}${search ? `?${search}` : ''}`}
          >
            {counterpartName(row)}
          </Link>
          {'candidate' in row && row.candidate.appliedPosition && (
            <div className="matching-secondary">{row.candidate.appliedPosition}</div>
          )}
          {'project' in row && !row.project.active && <div className="matching-secondary">Inactive project</div>}
          <div className="matching-mobile-meta matching-secondary">
            {budgetLabels[row.budgetStatus]} · {row.assessmentCoverage}% evidence
            {row.existingRecruitment ? ` · Recruitment: ${row.existingRecruitment.status.name}` : ''}
          </div>
          {Object.values(flags).some(Boolean) && (
            <div className="matching-row-flags">
              <RowStatusTags flags={flags} />
            </div>
          )}
        </>
      );
    },
  },
  {
    title: (
      <Tooltip title="Overall fit based on assessed criteria. Read the full assessment before making a decision.">
        Fit score
      </Tooltip>
    ),
    key: 'score',
    width: 110,
    render: (_, row) => (
      <span className="matching-score">
        {row.overallScore ?? '—'}
        {row.overallScore !== null && <small> /100</small>}
      </span>
    ),
  },
  {
    title: (
      <Tooltip title="Share of the criteria that could be assessed. A high score can still have limited evidence.">
        Evidence
      </Tooltip>
    ),
    key: 'coverage',
    className: 'matching-desktop-column',
    responsive: ['lg'],
    width: 120,
    render: (_, row) => <span>{row.assessmentCoverage}%</span>,
  },
  {
    title: 'Budget',
    key: 'budget',
    className: 'matching-desktop-column',
    responsive: ['lg'],
    width: 150,
    render: (_, row) =>
      row.budgetStatus === 'over_budget' ? (
        <Tag color="gold">Over budget</Tag>
      ) : (
        <span className="matching-secondary">{budgetLabels[row.budgetStatus]}</span>
      ),
  },
  {
    title: 'Recruitment',
    key: 'recruitment',
    className: 'matching-desktop-column',
    responsive: ['lg'],
    width: 170,
    render: (_, row) =>
      row.existingRecruitment ? (
        <Link
          to={pathToUrl(PageRoutesEnum.recruitmentEdit, { id: row.existingRecruitment.uuid })}
          target="_blank"
          rel="noopener noreferrer"
        >
          {row.existingRecruitment.status.name} ↗
        </Link>
      ) : (
        <span className="matching-muted">Not started</span>
      ),
  },
];
